import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const {FiDollarSign, FiFileText, FiTrendingUp, FiPieChart, FiBarChart2, FiCheckCircle, FiAward, FiCalendar} = FiIcons;

const FinancialReportingPage = () => {
  const reportingServices = [
    {
      icon: FiFileText,
      title: 'Monthly Financial Statements',
      description: 'Comprehensive monthly reports detailing all income, expenses, and property performance metrics.',
      features: ['Income & expense statements', 'Profit & loss reports', 'Cash flow analysis', 'Year-to-date comparisons']
    },
    {
      icon: FiPieChart,
      title: 'Performance Analytics',
      description: 'Detailed analysis of property performance with key metrics and trend identification.',
      features: ['ROI calculations', 'Cap rate analysis', 'Market comparisons', 'Performance benchmarks']
    },
    {
      icon: FiBarChart2,
      title: 'Budget & Forecasting',
      description: 'Strategic budget planning and financial forecasting for optimal investment planning.',
      features: ['Annual budget preparation', 'Capital expenditure planning', 'Revenue projections', 'Cost optimization']
    },
    {
      icon: FiTrendingUp,
      title: 'Tax Documentation',
      description: 'Complete tax preparation support with organized documentation and expense tracking.',
      features: ['Tax-ready statements', 'Expense categorization', 'Depreciation schedules', 'Year-end summaries']
    }
  ];

  const benefits = [
    'Real-Time Financial Access',
    'Professional CPA-Quality Reports',
    'Tax-Ready Documentation',
    'Performance Benchmarking',
    'Budget Variance Analysis',
    'Secure Online Portal Access'
  ];

  const reportTypes = [
    'Monthly Income Statements',
    'Quarterly Performance Reports',
    'Annual Financial Summaries',
    'Maintenance Expense Tracking',
    'Tax Preparation Documents',
    'Capital Improvement Reports'
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Financial Reporting Services Calgary",
    "description": "Professional financial reporting services for Calgary property owners including monthly statements, performance analytics, budget planning, and tax documentation.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      }
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "geoRadius": "50000"
    },
    "serviceType": ["Financial Reporting", "Property Accounting", "Tax Documentation", "Budget Planning", "Performance Analytics"]
  };

  return (
    <>
      <SEOHead
        title="Financial Reporting Services Calgary | Property Accounting"
        description="DGM Property Management offers professional financial reporting in Calgary. Monthly statements, performance analytics, tax documentation, and budget planning. CPA-quality reports. Call 587-800-1215."
        keywords="financial reporting Calgary, property accounting Calgary, rental property financial reports, Calgary property management reports, tax documentation Calgary, property performance analysis"
        canonical="/services/financial-reporting"
        ogImage="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Financial Reporting Services Calgary
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Professional financial reporting and accounting services with CPA-quality reports, 
                real-time access, and comprehensive tax documentation for your Calgary property investments.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Reporting Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Financial Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Professional accounting and reporting solutions designed specifically for Calgary real estate investors.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {reportingServices.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                      <div className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2">
                            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Report Types Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Professional Report Types</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive reporting solutions covering all aspects of your Calgary property investment.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reportTypes.map((report, index) => (
                <motion.div
                  key={report}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-lg p-6 text-center shadow-lg"
                >
                  <SafeIcon icon={FiFileText} className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900">{report}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-8">Why Choose Our Financial Reporting?</h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <SafeIcon icon={FiAward} className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <span className="text-lg text-gray-700">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                    alt="Professional financial reporting and property accounting services"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">Optimize Your Property Financial Management</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Our professional financial reporting services provide clear insights into your Calgary property performance. 
                Contact us today for comprehensive accounting solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Request Financial Consultation
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FinancialReportingPage;